<template #default>
  <div ref="mainLayout">
    <a-layout style="min-height: 100vh">
      <a-layout-sider v-model:collapsed="collapsed">
        <div class="logo" :class="{ 'is-collapsed': collapsed }">
          <img class="logo-img" src="@/assets/logo.png" alt="Logo" />
        </div>
        <a-menu
          v-model:selectedKeys="selectedKeys"
          theme="dark"
          mode="inline"
          :items="menuItems"
          @click="handleMenuClick"
        />
      </a-layout-sider>
      <a-layout>
        <a-layout-header class="header">
          <MenuUnfoldOutlined
            v-if="collapsed"
            class="trigger"
            @click="() => (collapsed = !collapsed)"
          />
          <MenuFoldOutlined
            v-else
            class="trigger"
            @click="() => (collapsed = !collapsed)"
          />
          <!-- <div class="logo" /> -->
          <a-dropdown trigger="click" class="header-dropdown">
            <a-button class="user-btn">
              <a-avatar size="small" class="user-avatar" :src="userAvatar" />
              <span class="user-name">{{ userName }}</span>
            </a-button>
            <template #overlay>
              <a-menu @click="handleLogout">
                <a-menu-item key="logout">
                  <LogoutOutlined />
                  Logout
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </a-layout-header>
        <a-layout-content style="margin: 0 16px">
          <a-breadcrumb style="margin: 16px 0">
            <a-breadcrumb-item
              v-for="(item, index) in breadcrumbs"
              :key="index"
            >
              {{ item }}
            </a-breadcrumb-item>
          </a-breadcrumb>
          <div
            :style="{ padding: '24px', background: '#fff', minHeight: '360px' }"
          >
            <router-view></router-view>
          </div>
        </a-layout-content>
        <a-layout-footer style="text-align: center">
          @PinPoint
        </a-layout-footer>
      </a-layout>
    </a-layout>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
// import { mapGetters } from "vuex";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  // UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons-vue'
import reference from '@/api/reference'
import fund from '@/api/fund'
import userAvatar from '@/assets/userAvator.png'

const router = useRouter()
const route = useRoute()
const store = useStore()
const collapsed = ref(false)
const selectedKeys = ref([])

watch(
  () => route.name,
  (newName) => {
    if (newName) {
      selectedKeys.value = [newName]
    }
  },
  { immediate: true }
)

const buildMenuItems = (routes) => {
  return routes
    .filter(
      (route) =>
        route.path !== '/login' &&
        (route.name || (route.children && route.children.length))
    )
    .map((route) => {
      const item = {
        key: route.name,
        icon: route.icon,
        label: route.meta?.title || route.name,
        path: route.path ? `/${route.path}`.replace(/\/+/, '/') : undefined,
      }
      if (route.children && route.children.length) {
        item.children = buildMenuItems(route.children)
      }
      return item
    })
}

const menuItems = computed(() => {
  const menus = router.options.routes || []
  return buildMenuItems(menus)
})

const breadcrumbs = computed(() => {
  return route.matched
    .filter((item) => item.meta?.title || item.name)
    .map((item) => item.meta?.title || item.name)
})

// Replaced userName function with a reactive computed property
const userName = computed(() => store.getters['auth/getUserName'] || 'Guest')

const handleMenuClick = (menu) => {
  if (menu?.key) {
    const findMenuItem = (items, key) => {
      for (const item of items) {
        if (item.key === key) return item
        if (item.children) {
          const found = findMenuItem(item.children, key)
          if (found) return found
        }
      }
      return null
    }
    const menuItem = findMenuItem(menuItems.value, menu.key)
    if (menuItem) {
      if (menuItem.path) {
        router.push(menuItem.path)
      } else if (menuItem.children && menuItem.children.length) {
        const firstChildWithPath = menuItem.children.find((child) => child.path)
        if (firstChildWithPath && firstChildWithPath.path) {
          router.push(firstChildWithPath.path)
        }
      }
    }
  }
}

// Add new logout event handler
const handleLogout = ({ key }) => {
  if (key === 'logout') {
    store.commit('auth/CLEAR_USER')
    store.commit('auth/CLEAR_AUTH')
    store.commit('auth/CLEAR_ROLE')
    router.push('/login')
  }
}

onMounted(async () => {
  try {
    const [
      userAccountResp,
      currencyResp,
      countryResp,
      countryCurrencyResp,
      fundResp,
    ] = await Promise.all([
      reference.getUserAccountList({ countryCode: null }),
      reference.getCurrencyList({ currencyCode: null }),
      reference.getCountryList({ countryCode: null }),
      reference.getCountryCurrencyList(),
      fund.getFundList({}),
    ])
    if (userAccountResp && userAccountResp.data) {
      store.commit('reference/SET_USER_ACCOUNT_LIST', userAccountResp.data)
    }
    if (currencyResp && currencyResp.data) {
      store.commit('reference/SET_CURRENCY_LIST', currencyResp.data)
    }
    if (countryResp && countryResp.data) {
      store.commit('reference/SET_COUNTRY_LIST', countryResp.data)
    }
    if (countryCurrencyResp && countryCurrencyResp.data) {
      store.commit(
        'reference/SET_COUNTRY_CURRENCY_LIST',
        countryCurrencyResp.data
      )
    }
    if (fundResp && fundResp.data) {
      store.commit('reference/SET_FUND_LIST', fundResp.data)
    }
  } catch (e) {
    console.error('Failed to fetch reference lists:', e)
  }
})
</script>

<style scoped>
.logo {
  height: 32px;
  margin: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-img {
  max-height: 32px;
  max-width: 128px;
  object-fit: contain;
}

.logo.is-collapsed .logo-img {
  max-width: 32px;
}

.site-layout .site-layout-background {
  background: #fff;
}

.header {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 0px;
}

.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 24px;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

.header-dropdown {
  margin-left: auto; /* 自动�?dropdown 推到最右侧 */
}

.user-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
}

.user-avatar {
  background: transparent;
}

.user-name {
  line-height: 1;
}

[data-theme='dark'] .site-layout .site-layout-background {
  background: #141414;
}
</style>
